const DATA_PLANE_URL = process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL || ''
const WRITE_KEY = process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY || ''

import { ApiObject, ApiOptions, RudderAnalytics } from '@rudderstack/analytics-js'
import { RefObject, useCallback, useSyncExternalStore } from 'react'

import { useCart } from '@/app/book/hooks/useCart'
import { brand } from '@/utils/brand'

// ---------------------------------------------------------------------------
// Module-singleton core. The SDK is a hard singleton whose second `.load()`
// is silently dropped (only the first call's onLoaded ever fires), so loading
// per hook instance left every other instance stuck at loaded=false. All SDK
// lifecycle state therefore lives here, owned by exactly one caller:
// <RudderstackAnalyticsBootstrap /> in AppProviders. The hook below is a pure
// consumer. Module state is plain data — no window access at module eval, so
// this file is safe to import during static-export prerender.
// ---------------------------------------------------------------------------

// Module-level handle on the loaded Rudderstack SDK so non-React code paths
// (e.g. fireLeadCapture in useBraze.ts, which is called from an iframe submit
// listener) can read the visitor's anonymous_id and forward it with API
// requests for session-stitching downstream in Rudderstack.
export const rudderModuleRef: { current: RudderAnalytics | null } = { current: null }

let loadStarted = false
let sdkLoaded = false
let lastTrackedPathname: string | null = null
const sdkLoadedSubscribers = new Set<() => void>()

export const subscribeToRudderstackLoaded = (onChange: () => void): (() => void) => {
  sdkLoadedSubscribers.add(onChange)
  return () => sdkLoadedSubscribers.delete(onChange)
}

export const isRudderstackLoaded = (): boolean => sdkLoaded

/**
 * Loads the SDK exactly once per full page load. The guard flips
 * synchronously before `.load()`, so StrictMode's doubled effects and any
 * accidental second caller are no-ops (the SDK would ignore a repeat load
 * anyway, but silently — its onLoaded would never fire).
 */
export const loadRudderstackOnce = (): void => {
  if (loadStarted) return
  if (typeof window === 'undefined' || !DATA_PLANE_URL || !WRITE_KEY) return
  loadStarted = true

  rudderModuleRef.current = new RudderAnalytics()
  rudderModuleRef.current.load(WRITE_KEY, DATA_PLANE_URL, {
    integrations: {
      'Optimizely Web': 'optimizely' in window,
    },
    onLoaded: () => {
      sdkLoaded = true
      sdkLoadedSubscribers.forEach((notify) => notify())
    },
  })
}

/**
 * Fires page() (+ 'Checkout Step Viewed' on /book/* steps) once per pathname.
 * Compare-last dedup: returning to a previously visited path re-fires, but
 * StrictMode doubles and re-renders on the same path do not.
 */
export const trackPageViewOnce = (
  pathname: string,
  step?: { name?: string; number?: number },
): void => {
  if (!rudderModuleRef.current || lastTrackedPathname === pathname) return
  lastTrackedPathname = pathname

  rudderModuleRef.current.page({ brand: brand.key, site_version: '2.0' })

  if (step?.name && step?.number)
    rudderModuleRef.current.track('Checkout Step Viewed', {
      brand: brand.key,
      step_name: step.name,
      step_number: step.number,
    })
}

export const getRudderstackAnonymousId = (): string | undefined => {
  const instance = rudderModuleRef.current
  if (!instance) return undefined
  try {
    return instance.getAnonymousId() || undefined
  } catch {
    return undefined
  }
}

/**
 * getRudderstackAnonymousId() bounded by a short wait, for pre-mutation call
 * sites (account creation, lead capture, newsletter, SMS opt-in) that want the
 * SDK id but must never hang the UX. The SDK only loads after window 'load'
 * plus a sourceConfig fetch, so a submit in the first seconds of a page can
 * run before it exists; racing the loaded signal against a timeout covers
 * that window. Mirrors cruises-next lib/analytics/rudderstack.ts
 * getAnonymousIdWithTimeout. Never rejects — resolves undefined on timeout.
 */
export const getRudderstackAnonymousIdWithTimeout = async (
  timeoutMs = 1000,
): Promise<string | undefined> => {
  const immediate = getRudderstackAnonymousId()
  // Nothing to wait for when the SDK is already loaded, or can never load
  // (keys unset, e.g. local builds) — avoid a pointless full timeout.
  if (immediate || isRudderstackLoaded() || !DATA_PLANE_URL || !WRITE_KEY) return immediate

  return new Promise((resolve) => {
    let unsubscribe: (() => void) | null = null
    const timer = setTimeout(() => {
      unsubscribe?.()
      resolve(getRudderstackAnonymousId())
    }, timeoutMs)
    unsubscribe = subscribeToRudderstackLoaded(() => {
      clearTimeout(timer)
      unsubscribe?.()
      resolve(getRudderstackAnonymousId())
    })
  })
}

// Fire a Rudderstack event from non-React code paths (e.g. the Decagon
// setEventListener callback in lib/decagon.ts). Uses the module-level SDK handle
// populated by useRudderstackAnalytics (mounted app-wide in ClientPortal), and
// injects `brand` to match the React hook's default props. No-op until the SDK
// has loaded; never throws (analytics must not break the page).
export const trackRudderstackEvent = (
  event: string,
  properties?: ApiObject,
  options?: ApiOptions,
): void => {
  const instance = rudderModuleRef.current
  if (!instance) return
  try {
    const payload = { brand: brand.key, ...properties }
    if (options) instance.track(event, payload, options)
    else instance.track(event, payload)
  } catch {
    /* swallow — analytics must never break the page */
  }
}

export type TrackingData = ApiObject

export interface UseRudderstackAnalytics {
  track?: (event: string, properties?: TrackingData, options?: ApiOptions) => void
  ref?: RefObject<RudderAnalytics | null>
  loaded: boolean
}

export const useRudderstackAnalytics = (): UseRudderstackAnalytics => {
  // Shared load signal: every instance — mounted before or after the SDK
  // loads — flips together, instead of only the instance that won the load
  // race (which broke e.g. the purchase event's `loaded` gate).
  const loaded = useSyncExternalStore(
    subscribeToRudderstackLoaded,
    isRudderstackLoaded,
    () => false,
  )
  const { cart } = useCart()

  const track = useCallback(
    // `options` carries the event-scoped `context` some destinations map their
    // user-data fields from (see src/utils/metaMatch.ts). Omitted entirely when
    // absent so every existing 2-arg call site keeps its exact call shape.
    (event: string, properties?: TrackingData, options?: ApiOptions) => {
      const payload = {
        brand: brand.key,
        cart_id: cart?.id,
        ...properties,
      }
      return options
        ? rudderModuleRef.current?.track(event, payload, options)
        : rudderModuleRef.current?.track(event, payload)
    },
    [cart?.id],
  )

  return { track, ref: rudderModuleRef, loaded }
}
